import { z } from 'zod';
import { schemaDebug } from '../../../utils/schemaLogger';
import { EmvDataSchema, ReceiptDataSchema, ReceiptObjectDataSchema } from '../common/Receipt';

// Amount validation (decimal format)
const AmountSchema = z.string()
  .max(12, { message: "Amount cannot exceed 12 characters" })
  .regex(/^\d+(\.\d{1,2})?$/, { message: "Amount must be a valid decimal number" })
  .optional();

// Order ID validation (note: orderId with capital I)
const OrderIdSchema = z.string()
  .max(50, { message: "Order ID cannot exceed 50 characters" })
  .optional();

// Currency validation (3-letter code)
const CurrencySchema = z.string()
  .length(3, { message: "Currency must be exactly 3 characters" })
  .regex(/^[A-Z]{3}$/, { message: "Currency must be 3 uppercase letters" })
  .optional();

// RetRef validation
const RetRefSchema = z.string()
  .max(32, { message: "RetRef cannot exceed 32 characters" })
  .optional();

// Merchant ID validation
const MerchantIdSchema = z.string()
  .max(16, { message: "Merchant ID cannot exceed 16 characters" })
  .optional();

// Token validation
const TokenSchema = z.string()
  .max(19, { message: "Token cannot exceed 19 characters" })
  .optional();

// Account validation (masked account number or token)
const AccountSchema = z.string()
  .max(27, { message: "Account cannot exceed 27 characters" })
  .optional();

// Response code validation
const RespCodeSchema = z.string()
  .max(3, { message: "Response code cannot exceed 3 characters" })
  .optional();

// Response processor validation
const RespProcSchema = z.string()
  .max(4, { message: "Response processor cannot exceed 4 characters" })
  .optional();

// Response status validation
const RespStatSchema = z.enum(['A', 'B', 'C']).optional();

// Response text validation
const RespTextSchema = z.string()
  .max(40, { message: "Response text cannot exceed 40 characters" })
  .optional();

// Settlement status - The wire can carry various formats, so we accept any string
const SetlStatSchema = z.string().optional();

// Auth date validation (YYYYMMDD format)
const AuthDateSchema = z.string()
  .regex(/^\d{8}$/, { message: "Auth date must be in YYYYMMDD format" })
  .optional();

// Last four validation
const LastFourSchema = z.string()
  .length(4, { message: "Last four must be exactly 4 characters" })
  .regex(/^\d{4}$/, { message: "Last four must be 4 digits" })
  .optional();

// Name validation
const NameSchema = z.string()
  .max(30, { message: "Name cannot exceed 30 characters" })
  .optional();

// Expiry validation (MMYY format)
const ExpirySchema = z.string()
  .regex(/^\d{4}$/, { message: "Expiry must be in MMYY format" })
  .optional();

// BIN type - The wire can carry various values, so we accept any string
const BinTypeSchema = z.string().optional();

// Entry mode validation
const EntryModeSchema = z.enum([
  'Keyed', 'Moto', 'ECommerce', 'Recurring', 'Swipe (Non EMV)',
  'DigitalWallet', 'Digital Wallet', 'EMVContact', 'Contactless', 'Fallback to Track', 'Fallback to Keyed'
]).optional();

// Fee auth code validation
const FeeAuthCodeSchema = z.string()
  .max(6, { message: "Fee auth code cannot exceed 6 characters" })
  .optional();

// Fee format validation
const FeeFormatSchema = z.enum(['flat', 'percent']).optional();

// Fee merchant ID validation
const FeeMerchIdSchema = z.string()
  .max(16, { message: "Fee merchant ID cannot exceed 16 characters" })
  .optional();

// Fee RetRef validation
const FeeRetRefSchema = z.string().optional();

// Fee type validation
const FeeTypeSchema = z.enum([
  'CVNFEE', 'SURCHRG', 'SRVCFEE', 'SRVC_WAIVED',
  'CVN_WAIVED', 'SURCHRG_WAIVED', 'FEE_TXN'
]).optional();

// Fee value validation
const FeeValueSchema = z.string().optional();

// User fields validation (array or object)
const UserFieldsSchema = z.union([
  z.string().transform((str) => {
    try {
      return JSON.parse(str);
    } catch {
      return [];
    }
  }),
  z.array(z.record(z.string(), z.any())),
  z.record(z.string(), z.any())
]).optional();

// Voidable validation
const VoidableSchema = z.enum(['Y', 'N']).optional();

// Refundable validation
const RefundableSchema = z.enum(['Y', 'N']).optional();

// Decline category validation
const DeclineCategorySchema = z.enum(['1', '2', '3', '4']).optional();

// Decline category text validation
const DeclineCategoryTextSchema = z.string().optional();

// Merchant advice code validation
const MerchAdviceCodeSchema = z.string()
  .max(2, { message: "Merchant advice code cannot exceed 2 characters" })
  .optional();

// Merchant advice text validation
const MerchAdviceTextSchema = z.string().optional();

const BaseInquireByOrderidResponseSchema = z.object({
  // Basic transaction information
  amount: AmountSchema,
  orderId: OrderIdSchema,
  currency: CurrencySchema,
  retref: RetRefSchema,
  merchid: MerchantIdSchema,
  token: TokenSchema,
  account: AccountSchema,

  // Response codes and status
  respcode: RespCodeSchema,
  respproc: RespProcSchema,
  respstat: RespStatSchema,
  resptext: RespTextSchema,
  setlstat: SetlStatSchema,

  // Card and account information
  authcode: z.union([
    z.string().max(6),
    z.number().int().positive()
  ]).optional(), // Authorization code
  authdate: AuthDateSchema,
  lastfour: LastFourSchema,
  name: NameSchema,
  expiry: ExpirySchema,
  bintype: BinTypeSchema,
  entrymode: EntryModeSchema,
  cof: z.enum(['C', 'M']).optional(), // Card on file type
  batchid: z.string().max(12).optional(), // Batch ID
  capturedate: z.string().regex(/^\d{14}$/).optional(), // Capture date/time
  settledate: z.string().regex(/^\d{14}$/).optional(), // Settlement date/time
  profileid: z.string().max(20).optional(), // Profile ID
  acctid: z.string().max(3).optional(), // Account ID within profile
  signature: z.string().max(6144).optional(), // Base64-encoded signature
  commcard: z.enum(['Y', 'N']).optional(), // Corporate card indicator
  cardproc: z.string().max(4).optional(), // Card processor

  // Fee information
  fee_authcode: FeeAuthCodeSchema,
  fee_format: FeeFormatSchema,
  fee_merchid: FeeMerchIdSchema,
  fee_retref: FeeRetRefSchema,
  fee_type: FeeTypeSchema,
  fee_value: FeeValueSchema,

  // EMV and receipt data - accept both string (from API) and object (already parsed)
  emvTagData: z.union([
    z.string().transform((str): z.infer<typeof EmvDataSchema> | undefined => {
      // The wire may carry emvTagData as a JSON string - parse it
      if (!str || str.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(str);
        const validated = EmvDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof EmvDataSchema>;
      } catch {
        schemaDebug('Failed to parse emvTagData JSON, returning undefined');
        return undefined;
      }
    }),
    EmvDataSchema, // Already an object (from DB or already parsed)
    z.undefined()
  ]).optional(),
  receipt: z.union([
    z.string().transform((str): z.infer<typeof ReceiptDataSchema> | undefined => {
      // The wire may carry receipt as a JSON string - parse it
      if (!str || str.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(str);
        const validated = ReceiptDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof ReceiptDataSchema>;
      } catch {
        schemaDebug('Failed to parse receipt JSON, returning undefined');
        return undefined;
      }
    }),
    ReceiptDataSchema, // Already an object (from DB or already parsed)
    z.undefined()
  ]).optional(),
  receiptObj: z.any().transform((value): z.infer<typeof ReceiptObjectDataSchema> | undefined => {
    // Handle undefined/null
    if (value === undefined || value === null) {
      return undefined;
    }

    // If it's already an object, validate and return it
    if (typeof value === 'object') {
      const validated = ReceiptObjectDataSchema.safeParse(value);
      return validated.success ? validated.data : value as z.infer<typeof ReceiptObjectDataSchema>;
    }

    // If it's a string, parse it as JSON
    if (typeof value === 'string') {
      if (value.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(value);
        const validated = ReceiptObjectDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof ReceiptObjectDataSchema>;
      } catch {
        schemaDebug('Failed to parse receiptObj JSON, returning undefined');
        return undefined;
      }
    }

    // Otherwise return undefined
    return undefined;
  }).optional(),
  userfields: UserFieldsSchema,

  // Transaction capabilities
  voidable: VoidableSchema,
  refundable: RefundableSchema,

  // Decline information
  declineCategory: DeclineCategorySchema,
  declineCategoryText: DeclineCategoryTextSchema,
  merchAdviceCode: MerchAdviceCodeSchema,
  merchAdviceText: MerchAdviceTextSchema
}).strict();

// Main InquireByOrderidResponse schema
const InquireByOrderidResponseSchema = BaseInquireByOrderidResponseSchema.transform(data => {
  let emvTagData = data.emvTagData;
  if (!emvTagData && data.receiptObj && data.receiptObj.emv) {
    emvTagData = data.receiptObj.emv;
  }
  return { ...data, emvTagData };
});

// Strict schema requiring core fields
const StrictInquireByOrderidResponseSchema = BaseInquireByOrderidResponseSchema.safeExtend({
  retref: z.string().min(1, { message: "RetRef is required" }),
  respstat: z.enum(['A', 'B', 'C']),
  respcode: z.string().min(1, { message: "Response code is required" })
}).transform(data => {
  let emvTagData = data.emvTagData;
  if (!emvTagData && data.receiptObj && data.receiptObj.emv) {
    emvTagData = data.receiptObj.emv;
  }
  return { ...data, emvTagData };
});

// API response schema (handles string JSON fields)
const ApiInquireByOrderidResponseSchema = z.object({
  amount: z.string().optional(),
  orderId: z.string().optional(),
  currency: z.string().optional(),
  retref: z.string().optional(),
  merchid: z.string().optional(),
  token: z.string().optional(),
  account: z.string().optional(),
  respcode: z.string().optional(),
  respproc: z.string().optional(),
  respstat: z.string().optional(),
  resptext: z.string().optional(),
  setlstat: z.string().optional(),
  authcode: z.union([z.string(), z.number()]).optional(),
  authdate: z.string().optional(),
  lastfour: z.string().optional(),
  name: z.string().optional(),
  expiry: z.string().optional(),
  bintype: z.string().optional(),
  entrymode: z.string().optional(),
  cof: z.string().optional(),
  batchid: z.string().optional(),
  capturedate: z.string().optional(),
  settledate: z.string().optional(),
  profileid: z.string().optional(),
  acctid: z.string().optional(),
  signature: z.string().optional(),
  commcard: z.string().optional(),
  cardproc: z.string().optional(),
  fee_authcode: z.string().optional(),
  fee_format: z.string().optional(),
  fee_merchid: z.string().optional(),
  fee_retref: z.string().optional(),
  fee_type: z.string().optional(),
  fee_value: z.string().optional(),
  emvTagData: z.union([z.string(), z.record(z.string(), z.any())]).optional(),
  receipt: z.union([z.string(), z.record(z.string(), z.any())]).optional(),
  receiptObj: z.union([z.string(), z.record(z.string(), z.any())]).optional(),
  userfields: z.union([z.string(), z.array(z.any()), z.record(z.string(), z.any())]).optional(),
  voidable: z.string().optional(),
  refundable: z.string().optional(),
  declineCategory: z.string().optional(),
  declineCategoryText: z.string().optional(),
  merchAdviceCode: z.string().optional(),
  merchAdviceText: z.string().optional()
})
  .transform((data) => {
    const result: Record<string, any> = {};

    // Copy string fields directly
    const stringFields = [
      'amount', 'orderId', 'currency', 'retref', 'merchid', 'token', 'account',
      'respcode', 'respproc', 'respstat', 'resptext', 'setlstat', 'authcode',
      'authdate', 'lastfour', 'name', 'expiry', 'bintype', 'entrymode', 'cof',
      'batchid', 'capturedate', 'settledate', 'profileid', 'acctid', 'signature', 'commcard', 'cardproc',
      'fee_authcode', 'fee_format', 'fee_merchid', 'fee_retref', 'fee_type', 'fee_value',
      'voidable', 'refundable', 'declineCategory', 'declineCategoryText',
      'merchAdviceCode', 'merchAdviceText'
    ];

    stringFields.forEach(field => {
      if ((data as any)[field] !== undefined) {
        result[field] = (data as any)[field];
      }
    });

    // Parse JSON fields
    if (data.emvTagData !== undefined) {
      if (typeof data.emvTagData === 'string') {
        try {
          result.emvTagData = JSON.parse(data.emvTagData);
        } catch {
          result.emvTagData = {};
        }
      } else {
        result.emvTagData = data.emvTagData;
      }
    }

    if (data.receipt !== undefined) {
      if (typeof data.receipt === 'string') {
        try {
          result.receipt = JSON.parse(data.receipt);
        } catch {
          result.receipt = {};
        }
      } else {
        result.receipt = data.receipt;
      }
    }

    if (data.receiptObj !== undefined) {
      if (typeof data.receiptObj === 'string') {
        try {
          result.receiptObj = JSON.parse(data.receiptObj);
        } catch {
          result.receiptObj = {};
        }
      } else {
        result.receiptObj = data.receiptObj;
      }
    }

    if (data.userfields !== undefined) {
      if (typeof data.userfields === 'string') {
        try {
          result.userfields = JSON.parse(data.userfields);
        } catch {
          result.userfields = [];
        }
      } else {
        result.userfields = data.userfields;
      }
    }

    return result;
  });

// Partial schema for updates
const PartialInquireByOrderidResponseSchema = BaseInquireByOrderidResponseSchema.partial().transform(data => {
  let emvTagData = data.emvTagData;
  if (!emvTagData && data.receiptObj && data.receiptObj.emv) {
    emvTagData = data.receiptObj.emv;
  }
  return { ...data, emvTagData };
});

// Empty response schema
const EmptyInquireByOrderidResponseSchema = z.object({}).strict();

// Validation helpers
const validateExpiry = (expiry: string): boolean => {
  return /^\d{4}$/.test(expiry);
};

const validateAuthDate = (authdate: string): boolean => {
  return /^\d{8}$/.test(authdate);
};

const validateAmount = (amount: string): boolean => {
  return /^\d+(\.\d{1,2})?$/.test(amount);
};

const validateLastFour = (lastfour: string): boolean => {
  return /^\d{4}$/.test(lastfour);
};

// Type exports
export type InquireByOrderidResponse = z.infer<typeof InquireByOrderidResponseSchema>;
export type StrictInquireByOrderidResponse = z.infer<typeof StrictInquireByOrderidResponseSchema>;
export type ApiInquireByOrderidResponse = z.infer<typeof ApiInquireByOrderidResponseSchema>;
export type PartialInquireByOrderidResponse = z.infer<typeof PartialInquireByOrderidResponseSchema>;
export type EmptyInquireByOrderidResponse = z.infer<typeof EmptyInquireByOrderidResponseSchema>;

// Schema exports
export {
  AccountSchema, AmountSchema, ApiInquireByOrderidResponseSchema, AuthDateSchema, BinTypeSchema, CurrencySchema, DeclineCategorySchema,
  DeclineCategoryTextSchema, EmptyInquireByOrderidResponseSchema, EntryModeSchema, ExpirySchema, FeeAuthCodeSchema,
  FeeFormatSchema,
  FeeMerchIdSchema,
  FeeRetRefSchema,
  FeeTypeSchema,
  FeeValueSchema, InquireByOrderidResponseSchema, LastFourSchema, MerchAdviceCodeSchema,
  MerchAdviceTextSchema, MerchantIdSchema, NameSchema, OrderIdSchema, PartialInquireByOrderidResponseSchema, RefundableSchema, RespCodeSchema,
  RespProcSchema,
  RespStatSchema,
  RespTextSchema, RetRefSchema, SetlStatSchema, StrictInquireByOrderidResponseSchema, TokenSchema, UserFieldsSchema, validateAmount, validateAuthDate, validateExpiry, validateLastFour, VoidableSchema
};

// Default export
export default InquireByOrderidResponseSchema;
